import { httpClient } from '@/api/httpClient';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import {
  mapApiResourceToConsentItem,
  mapApiResourceToConsentOverview,
  type ConsentCaptureItem,
  type ConsentOverview,
  type ConsentOverviewApiResponse,
  type ConsentOverviewFilter,
  type ConsentStatusItem,
  type ConsentStatusItemApiResource,
} from './consentsApi.types';

interface ConsentStatusApiResponse {
  items: ConsentStatusItemApiResource[];
}

function mapItems(data: ConsentStatusApiResponse): ConsentStatusItem[] {
  return data.items.map(mapApiResourceToConsentItem);
}

function toRequestBody(items: ConsentCaptureItem[]): Record<string, unknown> {
  return {
    items: items.map(function toApi(item) {
      return {
        document_id: item.documentId,
        document_version_id: item.documentVersionId,
        decision: item.decision,
      };
    }),
  };
}

export async function fetchClientConsents(clientId: string): Promise<ConsentStatusItem[]> {
  const response = await httpClient.get<ConsentStatusApiResponse>(
    '/clients/' + clientId + '/consents'
  );
  return mapItems(response.data);
}

export async function captureClientConsents(
  clientId: string,
  items: ConsentCaptureItem[]
): Promise<ConsentStatusItem[]> {
  const response = await httpClient.post<ConsentStatusApiResponse>(
    '/clients/' + clientId + '/consents',
    toRequestBody(items)
  );
  return mapItems(response.data);
}

export async function requestClientConsents(clientId: string): Promise<void> {
  await httpClient.post('/clients/' + clientId + '/consents/request');
}

export async function fetchConsentsOverview(
  page: number,
  search: string,
  overviewFilter: ConsentOverviewFilter
): Promise<ConsentOverview> {
  const response = await httpClient.get<ConsentOverviewApiResponse>(
    '/me/workspace/consents/overview',
    {
      params: {
        page: page,
        page_size: ADMIN_LIST_PAGE_SIZE,
        search: search.trim().length > 0 ? search.trim() : undefined,
        overview_filter: overviewFilter,
      },
    }
  );
  return mapApiResourceToConsentOverview(response.data);
}

export async function fetchOwnConsents(): Promise<ConsentStatusItem[]> {
  const response = await httpClient.get<ConsentStatusApiResponse>('/me/consents');
  return mapItems(response.data);
}

export async function withdrawOwnConsent(documentId: string): Promise<ConsentStatusItem[]> {
  const response = await httpClient.post<ConsentStatusApiResponse>(
    '/me/consents/' + documentId + '/withdraw'
  );
  return mapItems(response.data);
}

export async function captureOwnConsents(
  items: ConsentCaptureItem[]
): Promise<ConsentStatusItem[]> {
  const response = await httpClient.post<ConsentStatusApiResponse>(
    '/me/consents',
    toRequestBody(items)
  );
  return mapItems(response.data);
}
